import { useEffect, useState } from 'react';
import {
  Edit, SimpleForm, TextInput, SelectInput, BooleanInput, FormDataConsumer,
  useGetList, Loading, useNotify, useDataProvider, required,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { Stack, Typography, Box, Button, CircularProgress, Alert } from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import { ImageUploadInput } from '../../components/ImageUploadInput.jsx';
import { TiptapInput } from '../../components/TiptapInput.jsx';

const RESOURCE = 'home-popup';

const TYPES = [
  { id: 'text', name: 'Text only' },
  { id: 'image_text', name: 'Image + text' },
  { id: 'video', name: 'Video only' },
  { id: 'video_text', name: 'Text + video' },
];

const DEFAULTS = { isEnabled: false, type: 'text', title: 'Welcome to Maple Leaf' };

/**
 * Singleton — exactly one popup row. The "list" view routes straight to its
 * edit page; if no row exists yet we offer a one-click initialise.
 */
const PopupRedirect = () => {
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { data, isLoading } = useGetList(RESOURCE, {
    pagination: { page: 1, perPage: 1 },
    sort: { field: 'id', order: 'ASC' },
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (data && data.length > 0) navigate(`/${RESOURCE}/${data[0].id}`, { replace: true });
  }, [data, navigate]);

  if (isLoading) return <Loading />;
  if (data && data.length > 0) return null;

  const handleCreate = async () => {
    setCreating(true);
    try {
      const created = await dataProvider.create(RESOURCE, { data: { ...DEFAULTS } });
      navigate(`/${RESOURCE}/${created.data.id}`, { replace: true });
    } catch (err) {
      notify(`Failed to initialise popup: ${err.message}`, { type: 'error' });
      setCreating(false);
    }
  };

  return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Popup not yet initialised</Typography>
      <Button variant="contained" onClick={handleCreate} disabled={creating}>
        {creating ? <CircularProgress size={18} /> : 'Initialise popup'}
      </Button>
    </Box>
  );
};

const PopupForm = () => (
  <Stack spacing={2} sx={{ maxWidth: 720 }}>
    <Alert severity="info" sx={{ '& a': { color: 'inherit' } }}>
      A single popup shown on the homepage. Turn it off any time with the switch below — visitors
      see it once and again whenever you change the content.
    </Alert>

    <BooleanInput source="isEnabled" label="Show this popup on the homepage" />

    <SelectInput
      source="type"
      label="Popup type"
      choices={TYPES}
      validate={required()}
      defaultValue="text"
      helperText="Choose what the popup shows. Relevant fields appear below."
    />

    <TextInput source="title" label="Title" fullWidth />

    {/* Body text — for everything except video-only */}
    <FormDataConsumer>
      {({ formData }) => formData.type !== 'video' && (
        <Box>
          <Typography variant="overline" color="text.secondary">Body text</Typography>
          <TiptapInput source="body" label="" />
        </Box>
      )}
    </FormDataConsumer>

    {/* Image — only for image + text */}
    <FormDataConsumer>
      {({ formData }) => formData.type === 'image_text' && (
        <ImageUploadInput source="image" label="Image" />
      )}
    </FormDataConsumer>

    {/* Video — for video-only and text + video */}
    <FormDataConsumer>
      {({ formData }) => (formData.type === 'video' || formData.type === 'video_text') && (
        <TextInput
          source="videoUrl"
          label="Video URL"
          fullWidth
          helperText="Paste a YouTube or Vimeo link, or a direct .mp4 URL."
        />
      )}
    </FormDataConsumer>

    <Typography variant="overline" color="text.secondary" sx={{ pt: 1 }}>
      Button (optional)
    </Typography>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <TextInput source="ctaLabel" label="Button label" fullWidth helperText='e.g. "Apply now"' />
      <TextInput source="ctaUrl" label="Button link" fullWidth helperText='e.g. "/admission" or a full URL' />
    </Stack>
  </Stack>
);

const PopupEdit = () => (
  <Edit redirect={false} mutationMode="pessimistic" actions={false}>
    <SimpleForm><PopupForm /></SimpleForm>
  </Edit>
);

export default {
  list: PopupRedirect,
  edit: PopupEdit,
  icon: CampaignIcon,
  options: { label: 'Popup' },
};
